// ============================================================================
// products.js — what Blue Sky sells here, and what each product RESOLVES TO
// ============================================================================
// Two tables that answer two different questions, and one function tying them:
//   CATEGORIES — what a buyer is OFFERED (the gate's four cards + their kinds).
//                Also feeds Step 3's quote form, so it's what someone can be
//                QUOTED for, not just what they can design.
//   PRODUCTS   — what each of those RESOLVES TO: which code-generated 3D model,
//                with what options, keyed `${categoryId}|${kind}`. That's the
//                same pair the pills, the quote form, and ui/product-info.js's
//                brand/blurb records are already keyed by.
//   categoryIsDesignable() — a category is selectable iff every kind in it has a
//                model. Offered and designable are NOT the same thing, and the
//                gap between them is exactly where the old `live` flag went wrong.
//
// CATEGORIES lived in ui/select-weights-step.js until 2026-07-15. It moved here
// because that module imports .webp card art, so importing it from a node test is
// impossible — which meant nothing could check that PRODUCTS' keys actually match
// CATEGORIES' kinds. They're two hand-written lists of the same strings; a typo in
// either silently darkens a whole category, and the registry can't be the source
// of truth for "is this designable" while being unable to see what's offered.
// `test/ui-logic.smoke.mjs` now asserts they line up.
//
// WHY THIS EXISTS. `state.ui.product` used to be a BINARY — 'plate' | 'dumbbell'
// — resolved by `main.js` as `product !== 'dumbbell' ? 'plate' : 'dumbbell'`.
// That works for exactly two products and silently mis-resolves every other one:
// any category without its own model fell through to the bumper PLATE, so a
// buyer picking "Grip Plates" got a bumper plate on the stage and could request a
// quote off it. `select-weights-step.js` guarded that with `live: false` and a
// standing warning that "the flag is NOT the whole job — main.js:83 is a binary
// and has to become a real category→model map FIRST". This is that map.
//
// THE FLAG IS NOW DERIVED, NOT SET. `select-weights-step.js` asks
// `categoryIsDesignable()` rather than carrying a hand-maintained `live` field,
// so a category is selectable if and only if every kind in it resolves to a model
// here. The drift that flag existed to prevent is now structurally impossible:
// there is no second place to forget.
//
// ADDING A PRODUCT = ADDING A ROW. If it needs a new MODEL, that's a row in
// MODELS plus a geometry branch in `reveal-3d.js`'s build() — nothing else.
//
// ── AND, SINCE 2026-07-30, TWO MORE ANSWERS THE SAME TABLE OWES ─────────────
// `DESIGNABLE_WEIGHTS` (which denominations this product can actually be RENDERED
// at) and `productSizeIn` (how big it really is, in inches). Both exist for the
// Projects push — a rep picking "45s, 35s, 25s and 10s" for a customer's proposal
// packet — and both are DERIVED from the geometry charts rather than typed out, for
// the reason this file's whole header is about. See their own comments below.
// ============================================================================

import { BRAND_PUBLIC, BRAND_NAME } from './brand-flag.js';
import { COMPETITION_THICKNESS, ECONOMY_THICKNESS, DEFAULT_BUMPER_OPTS } from './geometry/bumper-plate.js';
import { GRIP_PLATE_DIMS_LB, gripPlateDims } from './geometry/grip-plate.js';
import { dumbbellHeadDims, isChartedDumbbellLoad } from './geometry/dumbbell.js';
import { barbellHeadDims, BARBELL_GRIP_LENGTHS } from './geometry/barbell.js';

/** Metres → inches. The charts are metric; a proposal page is not. */
const M_TO_IN = 1 / 0.0254;

/**
 * The four categories and the kinds inside each — Ryan's exact product list, the
 * real SKUs, not placeholders. Order IS the gate's on-screen order, left to right
 * (main.js's `pickedProducts` sorts by it to decide which product the tool opens
 * on), so don't reorder casually.
 *
 * ⚠ dumbbell|Premium Rubber is DELIBERATELY ABSENT (Ryan, 2026-07-15: "hide the
 * pill until modelled"). He's modelling it now. Standing it in with the Round
 * Rubber model was rejected — designing on, and quoting off, a model that isn't
 * the product is precisely the failure the old `live` flag guarded against.
 * Bringing it back = its kind here + a PRODUCTS row + its blurb in
 * product-info.js. It is NOT quotable meanwhile, the accepted cost of not
 * misleading.
 */
export const CATEGORIES = Object.freeze([
  { id: 'bumper', label: 'Bumper Plates', one: 'Bumper Plate', kinds: Object.freeze(['Competition Urethane', 'Competition Rubber', 'Training Rubber']) },
  { id: 'grip', label: 'Grip Plates', one: 'Grip Plate', kinds: Object.freeze(['Premium Urethane']) },
  { id: 'dumbbell', label: 'Dumbbells', one: 'Dumbbell', kinds: Object.freeze(['Round Urethane', 'Round Rubber']) },
  { id: 'barbell', label: 'Fixed Barbells', one: 'Fixed Barbell', kinds: Object.freeze(['Straight Bar Urethane', 'EZ Curl Bar Urethane', 'Straight Bar Rubber', 'EZ Curl Bar Rubber']) },
]);

/**
 * The product-line brand behind every designable weight — one house brand across
 * the whole catalogue (bumpers, grips, dumbbells, barbells), which is why the
 * switcher can show it as a constant prefix rather than per-product data. It leads
 * the switcher label ("Ballast Strength · Premium Urethane · Grip Plate", Ryan
 * 2026-07-17). If the catalogue ever splits into multiple lines this becomes a
 * per-category lookup — until then, one string is the whole truth.
 *
 * ⚠ EMPTY WHILE THE HOUSE LINE IS UNANNOUNCED (see brand-flag.js). The down-tab
 * drops the segment AND its separator on a falsy brand, so the pill reads
 * "Premium Urethane · Grip Plate" with no orphan dot. Flip BRAND_PUBLIC to get
 * the prefix back — nothing else here changes.
 */
export const PRODUCT_BRAND = BRAND_PUBLIC ? BRAND_NAME : '';

/**
 * The SINGULAR category label — `label` names a shelf ("Bumper Plates"), this
 * names one item on it ("Bumper Plate"). The product down-tab talks about the one
 * product on the stage, so it wants this: "Competition Rubber · Bumper Plate".
 *
 * Spelt out per row rather than derived by dropping a trailing "s". All four
 * happen to singularise that way today, which is exactly what makes the rule a
 * trap: it holds until the first category that doesn't ("Accessories"), and then
 * it doesn't announce itself — it just prints a typo in the UI.
 */
export function categoryLabelOne(categoryId) {
  const cat = CATEGORIES.find((c) => c.id === categoryId);
  return (cat && (cat.one || cat.label)) || categoryId;
}

/**
 * The code-generated 3D models, and the two things that follow from WHICH model a
 * product is rather than which product it is.
 *
 * `dyeable` — whether the buyer's plate colour (B6 §4.1) paints this body. Only
 *   the bumper dyes. Per render-engine's own contract: "dumbbells, barbells, grip
 *   plates are always black regardless of what the buyer's bumper is dyed." A
 *   non-dyeable product renders with `bodyColorOverride: DEFAULT_BODY_COLOR`; the
 *   design PROFILE keeps its dye, so switching back to the bumper is still dyed.
 *
 * `layoutFamily` — which set of `layouts.js` presets can be authored on this
 *   model's face. The bumper's 15 ring layouts assume a clean annulus at
 *   0.505–0.873R; the grip plate's hand cutouts sit at 0.599–0.8515R and would be
 *   printed straight across. The barbell shares the DUMBBELL's family because its
 *   heads ARE the dumbbell's (`createBarbellGeometry` delegates to
 *   `createDumbbellGeometry` and only stretches the handle).
 */
export const MODELS = Object.freeze({
  plate: Object.freeze({ dyeable: true, layoutFamily: 'plate' }),
  grip: Object.freeze({ dyeable: false, layoutFamily: 'grip' }),
  dumbbell: Object.freeze({ dyeable: false, layoutFamily: 'dumbbell' }),
  barbell: Object.freeze({ dyeable: false, layoutFamily: 'dumbbell' }),
});

/**
 * Every DESIGNABLE product: `${categoryId}|${kind}` → { model, geom }.
 *
 * `geom` is spread over the model's DEFAULT_*_OPTS by `reveal-3d.js`, so a row
 * carries only what differs from that model's defaults.
 *
 * ⚠ ABSENCE IS MEANINGFUL. A kind listed in CATEGORIES but missing here is NOT
 * designable, and its whole category goes unselectable on the gate (see
 * categoryIsDesignable). Don't add a row to make a pill light up — the row
 * asserts that a real model exists.
 */
export const PRODUCTS = Object.freeze({
  // ── Bumper plates ─────────────────────────────────────────────────────────
  // Urethane vs rubber is a FINISH: on the bumper it does NOT change geometry (it
  // IS the same plate shape — unlike the dumbbell, where `rubber` adds a raised
  // centre boss). But it DOES change the MATERIAL — urethane reads a step glossier,
  // rubber a satin matte — so `rubber` rides through here purely as the finish tell
  // that reveal-3d keys the body/brand-face roughness off (2026-07-16). The bumper
  // generator ignores the key (spread over its defaults), so the mesh is unchanged.
  'bumper|Competition Urethane': Object.freeze({ model: 'plate', geom: Object.freeze({ economy: false, rubber: false }) }),
  'bumper|Competition Rubber': Object.freeze({ model: 'plate', geom: Object.freeze({ economy: false, rubber: true }) }),
  // Training = the ECONOMY plate. Never stated outright, but Ryan's own blurb for
  // it — "Thicker, and the most affordable of the three" — describes the economy
  // variant exactly (no zinc hub, no bolts, a small metal bore flange, and a
  // thickness table that runs thicker than competition's at every weight). Rubber finish.
  'bumper|Training Rubber': Object.freeze({ model: 'plate', geom: Object.freeze({ economy: true, rubber: true }) }),

  // ── Grip plates ───────────────────────────────────────────────────────────
  // `rubber: false` is LOAD-BEARING, not decoration. reveal-3d derives the body
  // finish from `plate.rubber === false`, so an empty geom left `plate.rubber`
  // undefined and the strict-equality check fell to FALSE — rendering the grip
  // plate at rubber's satin 0.62 roughness. It is a URETHANE plate (supplier line
  // "PU Grip Plate", SKUs PUGP*), so it must read at urethane's glossy 0.38 like
  // every other urethane product in the set. Found 2026-07-29 while onboarding the
  // Ballast catalog against the real price list, which is also where the kind's
  // old name ("Premium Rubber") turned out to be wrong.
  'grip|Premium Urethane': Object.freeze({ model: 'grip', geom: Object.freeze({ rubber: false }) }),

  // ── Dumbbells ─────────────────────────────────────────────────────────────
  'dumbbell|Round Urethane': Object.freeze({ model: 'dumbbell', geom: Object.freeze({ rubber: false }) }),
  'dumbbell|Round Rubber': Object.freeze({ model: 'dumbbell', geom: Object.freeze({ rubber: true }) }),

  // ── Fixed barbells ────────────────────────────────────────────────────────
  // `kind` picks the handle (length + knurl markings + the EZ bend); `rubber`
  // rides through to the shared dumbbell head generator.
  'barbell|Straight Bar Urethane': Object.freeze({ model: 'barbell', geom: Object.freeze({ kind: 'straight', rubber: false }) }),
  'barbell|EZ Curl Bar Urethane': Object.freeze({ model: 'barbell', geom: Object.freeze({ kind: 'ezCurl', rubber: false }) }),
  'barbell|Straight Bar Rubber': Object.freeze({ model: 'barbell', geom: Object.freeze({ kind: 'straight', rubber: true }) }),
  'barbell|EZ Curl Bar Rubber': Object.freeze({ model: 'barbell', geom: Object.freeze({ kind: 'ezCurl', rubber: true }) }),
});

/* ============================================================================
 * ONE PRODUCT, PICKED MORE THAN ONCE — the INSTANCE suffix (Ryan 2026-08-09)
 * ============================================================================
 * A school buying white 45s and green 25s picks the SAME competition bumper twice, and each pick
 * carries its own plate colour and its own slice of the denomination ladder. Everything that
 * distinguishes those two picks has to ride the product key, because the key is what ~10 consumers
 * hold: the switcher orders by it, the quote form keys quantities `${key}|${weight}` off it, and
 * `state.ui.product` IS one.
 *
 * The shape is `category|kind` for the first pick and `category|kind#N` for the Nth. Ryan's answer
 * to the load-bearing question was "same design, the instance owns only its colour and its
 * denominations", so an instance is a second PICK of one product — never a second design.
 *
 * ⭐ THE FIRST INSTANCE HAS NO SUFFIX, AND THAT IS THE WHOLE MIGRATION STORY. Every autosave
 * envelope already in a buyer's localStorage, every `?d=` share link already sent, every
 * `DESIGNABLE_WEIGHTS` row, every quantity key on a half-filled quote form and every QA
 * `?product=` link stays byte-identical, because they all describe instance 1. There is no
 * version bump and nothing to migrate — a save written before today simply has no second pick.
 *
 * ⚠ EVERY REGISTRY LOOKUP MUST STRIP IT. `PRODUCTS`, `DESIGNABLE_WEIGHTS` and the geometry charts
 * are keyed by the BASE key and always will be: an instance is not a new product, it is the same
 * plate quoted twice. `productSpec` and `designableWeights` are the two doors into those tables,
 * so both call `baseProductKey` and every helper built on them (`modelFor`, `layoutFamilyFor`,
 * `geomOptsFor`, `productSupportsBodyColor`, `productSizeIn`, `isChartedWeight`, `designLadder`)
 * became instance-aware for free rather than each learning the suffix separately.
 */
const INSTANCE_SEP = '#';
/** `#` followed by digits, at the very end. Anchored so a kind could contain a `#` and survive. */
const INSTANCE_RE = /#(\d+)$/;

/**
 * The registry key for a {category, kind} pair. The ONE place the shape is spelt.
 * @param {number} [instance] 1-based pick number; 1 (the default) is spelt with NO suffix.
 */
export function productKey(categoryId, kind, instance = 1) {
  const n = Math.trunc(Number(instance));
  const suffix = Number.isFinite(n) && n > 1 ? `${INSTANCE_SEP}${n}` : '';
  return `${categoryId}|${kind}${suffix}`;
}

/**
 * Split a key back into its parts (the switcher labels off `kind`).
 * `kind` is always the BASE kind — the suffix never leaks into a label — and `instance` is 1 for
 * every key written before this existed, which is why the ~40 existing `{category, kind}`
 * destructures needed no edit.
 */
export function parseProductKey(key) {
  const s = String(key == null ? '' : key);
  const i = s.indexOf('|');
  if (i < 0) return { category: null, kind: null, instance: 1 };
  const rest = s.slice(i + 1);
  const m = INSTANCE_RE.exec(rest);
  const n = m ? Number(m[1]) : 1;
  return {
    category: s.slice(0, i),
    kind: m ? rest.slice(0, m.index) : rest,
    // A `#0`/`#1` written by hand normalises to 1 rather than becoming a phantom instance.
    instance: Number.isFinite(n) && n > 1 ? n : 1,
  };
}

/** The key with any instance suffix removed — what every REGISTRY is keyed by. */
export function baseProductKey(key) {
  return String(key == null ? '' : key).replace(INSTANCE_RE, '');
}

/**
 * Where a product key sits in the CATALOGUE order — the order the Pick Weights accordion lists
 * models in, which is `CATEGORIES` read top to bottom and each `kinds` array left to right:
 * Competition Urethane → Competition Rubber → Training Rubber → Grip Plate → Round Urethane →
 * Round Rubber → Straight Urethane → EZ Urethane → Straight Rubber → EZ Rubber.
 *
 * A rank, not a comparator, so callers can mix it into a sort that has other terms.
 *
 * ⚠ IT DELIBERATELY IGNORES THE INSTANCE AND THE WEIGHT. Two configurations of one bumper tie here,
 * which is the point: the caller settles them with a STABLE sort, so whatever ascending-by-weight
 * order the list already carried survives untouched. Ranking on weight as well would mean this
 * function needing a `state` to look rungs up in — and the one caller that has no state (a
 * recipient presenting somebody else's link) is exactly the one that needs the order most.
 *
 * ⚠ An unknown category or kind ranks LAST rather than first, so a stray or retired key can never
 * displace a real product to the front. Same rule as `pickedProducts`'s own `kindIdx`.
 */
export function catalogueRank(key) {
  const { category, kind } = parseProductKey(key);
  const ci = CATEGORIES.findIndex((c) => c.id === category);
  if (ci < 0) return Number.MAX_SAFE_INTEGER;
  const ki = CATEGORIES[ci].kinds.indexOf(kind);
  return ci * 1000 + (ki < 0 ? 999 : ki);
}

/**
 * Can this product be picked MORE THAN ONCE — i.e. does a second pick of it mean anything?
 *
 * The competition URETHANE bumper only, and it delegates rather than restating the rule: a second
 * configuration exists to hold a second PLATE COLOUR, so "can be picked twice" and "can be dyed"
 * are the same fact asked twice. (It read "competition bumpers" until 2026-08-31, when the factory
 * took the rubber one off colour — the delegation is why that sentence was the only thing to
 * change here and no caller of this needed touching.) Named separately because the UI reads far better for it and
 * because the day a non-dyeable product earns a second configuration, this is the one line that
 * changes instead of a scattering of `productSupportsBodyColor` calls that never meant colour.
 */
export function productSupportsMultiConfig(key) {
  return productSupportsBodyColor(key);
}

/**
 * The DEFAULT representative weight per category + unit (Ryan 2026-07-20). The designer no
 * longer offers a denomination picker — the plate/dumbbell/bar just opens on a sensible
 * standard weight (a design SAMPLE); the buyer's real weights + quantities are collected on
 * the quote form later. KG values are the nearest round competition size to the lb default.
 */
/* ⛔ THE `kg` COLUMN IS GONE, AND ITS REMOVAL IS THE POINT (Ryan 2026-08-02).
   It held "the nearest round competition size to the lb default" — 45 lb → 20 kg — and the LB/KG
   toggle used it to JUMP the buyer's denomination rather than convert it. That is what made a plate
   change size on a unit flip. `weightValue` is now always pounds and the unit only re-letters the
   face (design-profile: "UNIT IS A LABEL, NOT A WEIGHT"), so a per-unit sample would have nothing
   to mean: the 45 lb bumper simply reads 20.4 kg. */
export const DEFAULT_WEIGHT_BY_CATEGORY = Object.freeze({
  bumper: Object.freeze({ lb: 45 }),
  grip: Object.freeze({ lb: 45 }),
  /* 50 → 25 (Ryan 2026-08-08). Paired with the phone's Size grid dropping from three visible rows to
     two: the opening rung should sit in the part of the ladder the buyer can still see without
     scrolling, and on a 5–150 lb dumbbell 50 sat below the fold once the grid shrank. */
  dumbbell: Object.freeze({ lb: 25 }),
  barbell: Object.freeze({ lb: 60 }),
});
/** The sample weight to show for a product key, in POUNDS. Falls back to the bumper. */
export function defaultWeightFor(productKey) {
  const { category, instance } = parseProductKey(productKey);
  const row = DEFAULT_WEIGHT_BY_CATEGORY[category] || DEFAULT_WEIGHT_BY_CATEGORY.bumper;
  if (instance <= 1) return row.lb;
  /* ⭐ EACH CONFIGURATION OPENS ON A DIFFERENT RUNG (Ryan 2026-08-09): the 2nd on 35s, the 3rd on
     25s. A courtesy, not the enforcement — the buyer can move any of them, and the rule that stops
     two configurations claiming one rung lives on the quote form where a double-count costs money.
     But it is the thing that makes the feature feel obvious instead of confusing: open a second
     white-and-green set and it is ALREADY on a different plate, so there is nothing to explain. */
  const pref = configLadder(productKey);
  return pref[instance - 1] !== undefined ? pref[instance - 1] : row.lb;
}

/**
 * The order configurations claim denominations in: the product's own sample weight first, then
 * DOWN the ladder, then anything heavier that is left.
 *
 * On the competition bumper's `[10, 25, 35, 45, 55]` that reads 45 → 35 → 25 → 10 → 55, which is
 * exactly the 45/35/25 Ryan asked for and a sensible tail rather than a hard stop at three.
 * Derived from the ladder rather than typed, so a rung added to a product is picked up here for
 * free and the three numbers can never drift from the table they came out of.
 *
 * Going DOWN from the default and not up is the buyer's own order: a school splits its colours at
 * the heavy end (45s are the ones people look at), and the light change plates are the last thing
 * anybody gives a second colour to.
 *
 * ⚠ EXPORTED since 2026-08-10 (was `openingLadder`): the gate's denomination circles claim rungs in
 * exactly this order, so the picker and the seeding cannot disagree about which rung a new
 * configuration takes. One list, two consumers — this file's own recurring lesson.
 */
export function configLadder(key) {
  const ladder = designLadder(key);            // ascending, or null for free entry
  const { category } = parseProductKey(key);
  const base = (DEFAULT_WEIGHT_BY_CATEGORY[category] || DEFAULT_WEIGHT_BY_CATEGORY.bumper).lb;
  if (!ladder || !ladder.length) return [base];
  const below = ladder.filter((w) => w < base).sort((a, b) => b - a);   // 35, 25, 10
  const above = ladder.filter((w) => w > base).sort((a, b) => a - b);   // 55
  const start = ladder.includes(base) ? [base] : [];
  return [...start, ...below, ...above];
}

/**
 * How many configurations of this product are worth having — its ladder length, because
 * configurations PARTITION the denominations and a configuration with no rung left to claim is a
 * block on the quote form that can never carry a number.
 *
 * 1 for everything that cannot be picked twice, so a caller can compare against it without first
 * asking whether multi-configuration applies at all.
 */
export function maxConfigsFor(key) {
  if (!productSupportsMultiConfig(key)) return 1;
  const ladder = designLadder(key);
  return ladder && ladder.length ? ladder.length : 1;
}

/**
 * THE HOUSE FIXED-BARBELL SET — ten bars, 20 through 110 lb in tens.
 *
 * Ryan 2026-07-31: *"you design one fixed barbell, and then that button is always available to
 * click, which is your way of telling the tool that the customer plans to buy the full 10x barbell
 * set from 20-110 lbs."* Which is also the set changelog 185 was built from — a customer quoting
 * 20→110 in 10s, ten bars.
 *
 * ⚠ IT LIVES HERE, NOT IN `plate-batch.js`, and that is load-bearing rather than tidy. plate-batch
 * is DYNAMICALLY imported (it pulls in reveal-3d + the Three.js texture pipeline, and the whole
 * point is keeping that out of the entry bundle for every buyer who never opens the send dialog).
 * The dialog needs this list to LABEL the fan chip, so importing it from there would drag the 3D
 * chunk into the entry bundle to read ten numbers. It is product data anyway — the same kind of
 * fact as `DESIGNABLE_WEIGHTS` above it.
 *
 * ⚠ LB ONLY, safe today rather than by luck: nothing in the push pipeline carries a unit, so
 * `planJobs` resolves every job to 'lb'. **If kg is ever wired through, this needs a kg ladder** —
 * converting these rungs gives 9.07, 13.6, 18.1 kg, which is not a set anyone sells. A product
 * decision, not a rounding one.
 */
export const BARBELL_HOUSE_SET_LB = Object.freeze([20, 30, 40, 50, 60, 70, 80, 90, 100, 110]);

/**
 * Which bars a fanned set actually draws.
 *
 * The picked denominations when there are at least two — a rep quoting a PARTIAL set (a customer
 * who wants 20 through 60) must get exactly that. Otherwise the house set, because pressing
 * "One fanned set" on a single bar is the rep saying *they're buying the whole rack*, which is the
 * entire point of the button. A one-bar "fan" is therefore impossible, which is what the old
 * two-weight gate was really protecting against.
 */
export function fanWeightsFor(weights) {
  const picked = (Array.isArray(weights) ? weights : [])
    .map(Number).filter((w) => Number.isFinite(w) && w > 0);
  return picked.length >= 2 ? picked : [...BARBELL_HOUSE_SET_LB];
}

/**
 * The full spec for a product key, or null if it isn't designable.
 * @returns {{model: string, geom: object, dyeable: boolean, layoutFamily: string}|null}
 */
export function productSpec(key) {
  // `baseProductKey`: an instance is the same plate quoted twice, so it builds the same geometry,
  // wears the same layout family and is dyeable on the same terms. See the INSTANCE block above.
  const rec = PRODUCTS[baseProductKey(key)];
  if (!rec) return null;
  const model = MODELS[rec.model];
  return { model: rec.model, geom: rec.geom, dyeable: model.dyeable, layoutFamily: model.layoutFamily };
}

/** True if this {category, kind} has a 3D model behind it. */
export function isDesignable(categoryId, kind) {
  return !!PRODUCTS[productKey(categoryId, kind)];
}

/**
 * True if EVERY kind in a category is designable — i.e. the category card is
 * selectable on the gate. This replaces the hand-set `live` flag: liveness now
 * follows from geometry existing, which is the only thing it ever meant.
 * @param {{id: string, kinds: ReadonlyArray<string>}} cat
 */
export function categoryIsDesignable(cat) {
  return !!cat && Array.isArray(cat.kinds) && cat.kinds.length > 0 && cat.kinds.every((k) => isDesignable(cat.id, k));
}

/** Which 3D model a key builds ('plate' | 'grip' | 'dumbbell' | 'barbell'). */
export function modelFor(key) {
  return productSpec(key)?.model || null;
}

/** Which `layouts.js` family a key can be designed with ('plate' | 'grip' | 'dumbbell'). */
export function layoutFamilyFor(key) {
  return productSpec(key)?.layoutFamily || 'plate';
}

/** The geometry option overrides for a key (spread over the model's defaults). */
export function geomOptsFor(key) {
  return productSpec(key)?.geom || {};
}

/**
 * True if the buyer's plate colour paints this product's body — gates BOTH the "Plate
 * color" UI and the render override (a non-dyeable product renders black; main.js §545).
 * Keyed by PRODUCT (what callers hold); `reveal-3d.js` has a model-keyed twin for its own
 * internals. Both read MODELS, so the model tier can't disagree.
 *
 * ⛔ COLOUR IS A URETHANE CAPABILITY, NOT A PLATE ONE (the factory, via Ryan, 2026-08-31:
 * *"the customizable colors on comp bumpers are actually only available on urethane, not
 * rubber"*). Dye is something the URETHANE pour can carry; rubber is compounded black and
 * comes out black whatever tier of plate it is. So the rule is not about which bumper a buyer
 * picked — it is about what the body is MADE OF, and `bumper|Competition Urethane` is the only
 * product in the catalogue that is both a dyeable model and a urethane one.
 *
 * That makes this ONE rule rather than a growing list of exclusions: the model must take dye at
 * all (`MODELS.plate`; every other model is already black at the model tier), and the finish
 * must be urethane. `geom.rubber` is the finish tell every plate row already carries for
 * reveal-3d's roughness, so the finish is read off the registry rather than restated here.
 *
 * ⭐ IT SUPERSEDES THE OLD ECONOMY EXCLUSION AND SUBSUMES IT. Until today this said *"only the
 * COMPETITION bumpers (Urethane / Rubber) are offered plate colour"* (Ryan 2026-07-20) and
 * excluded the economy Training Rubber with `geom.economy === true`. Competition-vs-economy was
 * only ever a PROXY for the real fact — the economy plate is rubber, so the finish test excludes
 * it on its own, and the `economy` branch is gone rather than left sitting alongside as a second
 * reason for the same answer. If an economy URETHANE plate is ever sold this rule offers it
 * colour, which is the correct answer and not a hole.
 *
 * ⚠ `=== false`, not `!== true`: a missing or unrecognised finish reads as NOT urethane and the
 * product is denied colour. Promising a dye the factory cannot pour is the expensive direction of
 * this mistake; a designable plate that renders black is not.
 */
export function productSupportsBodyColor(key) {
  const spec = productSpec(key);
  if (spec?.dyeable !== true) return false;   // the model can take dye at all
  return spec.geom?.rubber === false;         // … and this product's body is urethane
}

/** Every designable key, in CATEGORIES order — the switcher's source of truth. */
export function designableKeys() {
  return Object.keys(PRODUCTS);
}

// ============================================================================
// WHAT THE DESIGNER CAN RENDER, AND HOW BIG IT REALLY IS
// ============================================================================
// Both tables below exist for the Projects push (`ui/project-push.js` → the rep
// picks the denominations a customer actually asked for, each becomes a plate on a
// proposal page). They are the third and fourth things this file answers, and they
// belong here for the same reason CATEGORIES does: one place, asserted in a test.
// ============================================================================

/**
 * The denominations a product can be RENDERED at — NOT the price list.
 *
 * ⚠ THIS IS THE POINT OF THE WHOLE TABLE. `plateThickness()` (geometry/bumper-plate.js)
 * and `gripPlateDims()` (geometry/grip-plate.js) both fall back to the NEAREST charted
 * row when a weight isn't in their table, and `dumbbellHeadDims()` does the same after
 * mapping lb → kg through `LB_TO_KG`. That fallback is right at runtime — a design
 * resumed at an odd weight should still draw something — and it is completely SILENT.
 * So offering a denomination the chart has never heard of does not fail: it renders a
 * different plate, correctly labelled, and nobody finds out until a customer measures a
 * proposal. This list is the gate that stops that, and `test/ui-logic.smoke.mjs` asserts
 * every entry lands on a real charted row with no fallback taken.
 *
 * ORDER IS HEAVIEST-FIRST, and it is content, not styling: a rep reads a proposal top
 * down, and Ryan's own phrasing for what he wants out of this ("45 on one, 35 on
 * another, 25, 10") is that order. The chip row in the push dialog renders it as given.
 *
 * WHAT'S DELIBERATELY MISSING, and why it is not a bug:
 *   • ~~grip 2.5 / 5 lb~~ — MODELLED 2026-07-31. They were the standing example of this
 *     whole warning: sellable, uncharted, and silently rendering as a 10 lb. They are
 *     charted now, with their own cutout-free geometry and zone.
 *   • ~~competition 10 lb~~ — ADDED 2026-08-02. It is on the price list (PUC10LB / PUB10LB)
 *     and `COMPETITION_THICKNESS` now carries a row for it; see that table's own warning,
 *     the figure is DERIVED pending Ryan's measurement.
 *   • competition 15 lb — still absent, and still real: there is no 15 under "PU Bumper
 *     Plate" on the price list. The ECONOMY plate does carry 15, which is why Training
 *     Rubber's list remains the longest of the three. That asymmetry is not a gap.
 *
 * `null` = FREE ENTRY, not "undesignable". A fixed barbell has no denomination chart of
 * its own: `barbellHeadDims` subtracts the handle's steel and reads the DUMBBELL chart at
 * whatever load is left, so any weight resolves to a sensible head. Written as an explicit
 * null rather than an absent key, because an absent key reads as "this product can't be
 * pushed at all" — which is the opposite of what it means.
 *
 * @type {Readonly<Record<string, ReadonlyArray<number>|null>>}
 */
/* The dumbbell racks, generated from the rule so the list cannot drift from the sentence that
   describes it. HEAVIEST-FIRST like every other entry — see the ORDER note above; the Size ladder
   in the designer sorts ascending for itself, so this order is the push dialog's, not the UI's. */
const DUMBBELL_FIVES = Object.freeze(Array.from({ length: 30 }, (_, i) => 150 - i * 5)); // 150 → 5
const DUMBBELL_WITH_HALVES = Object.freeze(
  [...DUMBBELL_FIVES, 27.5, 22.5, 17.5, 12.5, 7.5].sort((a, b) => b - a),
);

export const DESIGNABLE_WEIGHTS = Object.freeze({
  /* THE 10 IS REAL ON THE URETHANE AND A CROSS-SELL ON THE RUBBER (Ryan 2026-08-02).
     The price list carries PUC10LB / PUB10LB under "PU Bumper Plate" and nothing at 10 under
     "Rubber Competition Plate" — so the urethane genuinely has one and the rubber does not.
     Ryan: *"it's the one we'll sell as the 10 lber if someone orders the rubber competition and
     they want 10's with that too… no need for a disclosure, we tell people when they purchase."*
     So BOTH lists carry it deliberately: the designer's job is to show a buyer the set they will
     receive, and a rubber-competition buyer receives this plate as their 10. Do not "correct" the
     rubber row against the price list — the asymmetry is known and the answer is a sales one. */
  'bumper|Competition Urethane': Object.freeze([55, 45, 35, 25, 10]),
  'bumper|Competition Rubber': Object.freeze([55, 45, 35, 25, 10]),
  // ECONOMY_THICKNESS: the same four plus the 10 and 15 lb change plates.
  'bumper|Training Rubber': Object.freeze([55, 45, 35, 25, 15, 10]),
  // GRIP_PLATE_DIMS_LB — all six, since 2026-07-31. The 2.5 and 5 are the CUTOUT-FREE
  // plates (no hand holes, plain annular face); `isSmallGrip` is what tells them apart.
  'grip|Premium Urethane': Object.freeze([45, 35, 25, 10, 5, 2.5]),
  /* THE REAL RACKS (Ryan 2026-08-02) — superseding the 5–55 placeholder that stood while the
     price list's Bumper Plate tab was the only one parsed. His words: *"the rubber dumbbells go
     from 5 to 150 lb in 5 lb increments, plus there are also 7.5, 12.5, 17.5, 22.5, 27.5 lb
     options too. Then the urethane comes in 5-150 lb in 5 lb increments only."*
       The half-steps are a RUBBER-ONLY range, not an oversight on the urethane row — they are the
     light end of a rack where a 2.5 lb jump matters and nobody makes urethane that small.
       Built rather than typed: 35 numbers spelled out invite a transposed digit that no test would
     catch (every one of them IS a legal denomination, just not the right set), and stating the
     rule once means the ordering note above is enforced instead of merely described. */
  'dumbbell|Round Urethane': DUMBBELL_FIVES,
  'dumbbell|Round Rubber': DUMBBELL_WITH_HALVES,
  // Free entry — see the note above.
  'barbell|Straight Bar Urethane': null,
  'barbell|EZ Curl Bar Urethane': null,
  'barbell|Straight Bar Rubber': null,
  'barbell|EZ Curl Bar Rubber': null,
});

/**
 * The denominations to OFFER for a key: an array, or `null` for free entry.
 * An unknown key answers `null` too — a caller with a key this file doesn't know is in no
 * position to be told "these four and no others".
 */
/**
 * THE LADDER A BUYER PICKS FROM, in the order they see it. One function, because two surfaces now
 * show the same list and they were drifting (Ryan 2026-08-05: *"I want the list of the weights on
 * the form to mirror the order of which they were listed when designing on the customize tab -- they
 * always maintain a particular order, no reason to change it up now on the last step and have people
 * be confused"*).
 *
 * Two things it settles that `designableWeights` deliberately does not:
 *
 *   ⚠ THE FIXED BARBELL HAS NO CHART OF ITS OWN and answers `null` — which means FREE ENTRY, not
 *     "no denominations": its heads read the dumbbell's at whatever load the handle leaves. So it
 *     offers the house rack, the same ten bars the send-to-project fan assumes a bar buyer is
 *     buying. The Customize tab has always done this; the quote form did not, and a buyer who
 *     picked Fixed Barbells got a quantity block with no rows in it — no way to say what they
 *     wanted, on the step where saying it is the entire point.
 *
 *   ⚠ LIGHTEST FIRST (Ryan 2026-08-02: *"I want them to always be from lowest to highest in terms
 *     of number"*). `DESIGNABLE_WEIGHTS` is authored per product and the orders disagree — the
 *     bumper and grip tables descend, the house barbell set ascends — so sorting here is what makes
 *     every product read the same way round whatever its own table says.
 *
 * @returns {number[]|null} null when there is genuinely nothing to choose between.
 */
export function designLadder(key) {
  const list = designableWeights(key)
    || (parseProductKey(key).category === 'barbell' ? BARBELL_HOUSE_SET_LB : null);
  if (!list || !list.length) return null;
  return [...list].sort((a, b) => a - b);
}

export function designableWeights(key) {
  /* The FULL ladder the product can render, instance or not — deliberately not narrowed to the
     rungs this instance claims. The Size ladder shows the whole thing and greys what a sibling
     already owns (hiding it makes the tool look broken), and `weightForVisit` re-validates a
     remembered denomination against it. Ownership is a separate question, asked separately. */
  const list = DESIGNABLE_WEIGHTS[baseProductKey(key)];
  return list === undefined ? null : list;
}

/**
 * The product's real OUTER DIMENSION in inches — the basis for true relative scale on a
 * packet page, so a 10 lb grip prints smaller than a 45 lb one because it IS smaller.
 *
 * ⚠ IT HAS TO TRAVEL WITH THE TILE; IT CANNOT BE MEASURED FROM THE PICTURE. `portraitPNG`
 * re-fits the camera per weight and then crops to the product's alpha, which normalises
 * size away on purpose — two grip plates 1.67× apart come back as PNGs of nearly the same
 * pixel size. The PDF generator therefore scales each cell by `sizeIn / maxSizeIn` on the
 * page, and this is where that number comes from.
 *
 * Every branch reads a GEOMETRY chart rather than restating one:
 *   • bumper — 450 mm at EVERY denomination. Only thickness changes with weight
 *     (COMPETITION_THICKNESS / ECONOMY_THICKNESS), and thickness is edge-on in a portrait,
 *     so the number is flat 17.72" for all of them. Taken off DEFAULT_BUMPER_OPTS.outerRadius
 *     rather than typed, so a change to the plate's diameter reaches this automatically.
 *   • grip — the one product whose DIAMETER moves with weight (10.63" → 17.72"), through
 *     `gripPlateDims`.
 *   • dumbbell / barbell — the HEAD's diameter. The barbell reads the same chart through its
 *     own head-load correction, with the grip length taken from its KIND exactly the way
 *     `reveal-3d.setPlateForWeight` does (the default is the straight bar's, which would size
 *     an EZ bar's heads off the wrong handle).
 *
 *     ⚠ AN APPROXIMATION, and knowingly so. A portrait of a dumbbell is bounded by its
 *     LENGTH, not its head diameter — the grip between the heads is a near-constant, so head
 *     diameter OVERSTATES how much a set actually varies (a 5 lb next to a 55 lb will read a
 *     little smaller than life). It is exact for the three PLATE products, which are discs
 *     whose widest dimension IS the diameter, and those are what this feature was built for.
 *     If a dumbbell page ever reads wrong, the honest number is `gripLength + 2 × headThickness`
 *     — both already on the chart — and the fix is this branch alone. Left as-is rather than
 *     changed blind, because the difference is only judgeable against a real render.
 *
 * @param {string} key       a PRODUCTS key
 * @param {number} weight    the denomination
 * @param {'lb'|'kg'} [unit] defaults to lb — DESIGNABLE_WEIGHTS is an lb ladder
 * @returns {number} inches, 2dp (the worker clamps `sizeIn` to 2dp too), or 0 for an
 *   unknown key / nonsense weight. 0 means "don't scale me", which is what a tile with no
 *   `sizeIn` already meant, so a caller that forwards it is not creating a new case.
 */
export function productSizeIn(key, weight, unit = 'lb') {
  const spec = productSpec(key);
  const w = Number(weight);
  if (!spec || !Number.isFinite(w) || w <= 0) return 0;
  const round2 = (m) => Math.round(m * M_TO_IN * 100) / 100;
  switch (spec.model) {
    case 'plate':
      return round2(2 * DEFAULT_BUMPER_OPTS.outerRadius);
    case 'grip':
      // gripPlateDims is lb-only by charter ("grip plates are spec'd in lb").
      return round2(2 * gripPlateDims(w)[0]);
    case 'dumbbell':
      return round2(2 * dumbbellHeadDims(w, unit)[0]);
    case 'barbell':
      return round2(2 * barbellHeadDims(w, unit, {
        gripLength: BARBELL_GRIP_LENGTHS[spec.geom.kind === 'ezCurl' ? 'ezCurl' : 'straight'],
      })[0]);
    default:
      return 0;
  }
}

/**
 * Is `weight` a row the chart actually holds for this key — i.e. would resolving it take
 * the nearest-row FALLBACK? Exists so the smoke test can assert DESIGNABLE_WEIGHTS against
 * the geometry, and so nothing has to duplicate the "which chart does this model read"
 * decision that `productSizeIn` already makes.
 *
 * Free-entry products (the barbell) answer TRUE for anything: their head size is derived
 * from load, so there is no row to miss.
 */
export function isChartedWeight(key, weight, unit = 'lb') {
  const spec = productSpec(key);
  const w = Number(weight);
  if (!spec || !Number.isFinite(w) || w <= 0) return false;
  switch (spec.model) {
    case 'plate':
      return (spec.geom.economy === true ? ECONOMY_THICKNESS : COMPETITION_THICKNESS)[w] != null;
    case 'grip':
      return GRIP_PLATE_DIMS_LB[w] != null;
    case 'dumbbell':
      /* ⚠ A SPAN TEST NOW, NOT A MEMBERSHIP TEST. This asked whether the weight had an entry in
         `LB_TO_KG` AND that entry named a chart row — the right question while the lookup snapped
         to a knot, and the wrong one since it interpolates: 7.5 lb is charted in every sense that
         matters (it lands between two real rows) and is in neither table. What still needs
         catching is a load ABOVE the top row, where there is nothing to interpolate between and
         the head silently clamps. `isChartedDumbbellLoad` is that question, asked where the chart
         lives rather than restated here. */
      return isChartedDumbbellLoad(w, unit);
    case 'barbell':
      return true; // free entry — the head chart is read by LOAD, not by denomination
    default:
      return false;
  }
}
